import React from 'react'
import { ChevronRight } from 'lucide-react'

interface MobileTableCardProps<T> {
  item: T
  columns: Array<{
    key: string
    title: string
    render?: (value: any, item: T, index: number) => React.ReactNode
    mobileLabel?: string
    hideMobile?: boolean
  }>
  onRowClick?: (item: T, index: number) => void
  keyField: string
  index: number
  isSelected?: boolean
  onSelectRow?: (id: string) => void
}

export function MobileTableCard<T extends Record<string, any>>({
  item,
  columns,
  onRowClick,
  keyField,
  index,
  isSelected = false,
  onSelectRow
}: MobileTableCardProps<T>) {
  const visibleColumns = columns.filter(col => !col.hideMobile)

  const mainColumn = visibleColumns[0]
  const mainValue = mainColumn?.render
    ? mainColumn.render(item[mainColumn.key], item, index)
    : item[mainColumn.key]

  const secondaryColumns = visibleColumns.slice(1)

  return (
    <div
      onClick={() => onRowClick?.(item, index)}
      className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {onSelectRow && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectRow(item[keyField])}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        )}

        <div className="flex-1 flex items-center justify-between">
          <div className="font-medium text-gray-900 text-base">
            {mainValue}
          </div>
          {onRowClick && (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
          )}
        </div>
      </div>

      {secondaryColumns.length > 0 && (
        <div className="space-y-2">
          {secondaryColumns.map((column) => {
            const value = item[column.key]
            const content = column.render
              ? column.render(value, item, index)
              : value

            if (!content && content !== 0) return null

            return (
              <div key={column.key} className="flex justify-between text-sm gap-2">
                <span className="text-gray-500 flex-shrink-0">
                  {column.mobileLabel || column.title}:
                </span>
                <span className="text-gray-900 font-medium text-right">
                  {content}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
